<script>
  import { routeStore } from '../lib/stores/routeStore.js';
  import { processPostcode } from '../lib/utils/postcodeValidator.js';
  import Button from '../lib/components/Button.svelte';
  import Input from '../lib/components/Input.svelte';
  import Textarea from '../lib/components/Textarea.svelte';
  import Card from '../lib/components/Card.svelte';

  export let onOptimize;
  export let isOptimizing = false;
  export let onBackToHome;

  let bulkInput = '';
  let singleInput = '';
  let startInput = '';
  let endInput = '';
  let postcodes = [];
  let errors = {};
  let startLocation = null;
  let endLocation = null;
  let useCurrentLocation = false;
  let useCurrentLocationAsEnd = false;
  let isLocating = false;

  $: canOptimize = postcodes.length >= 2 && (startLocation || useCurrentLocation) && !isOptimizing;

  function handleBulkAdd() {
    const lines = bulkInput.split('\n').map(line => line.trim()).filter(Boolean);
    const validPostcodes = [];
    const newErrors = {};

    lines.forEach((line, index) => {
      const result = processPostcode(line);
      if (result.valid) {
        validPostcodes.push(result.formatted);
      } else {
        newErrors[`bulk-${index}`] = result.error;
      }
    });

    if (validPostcodes.length > 0) {
      postcodes = [...postcodes, ...validPostcodes];
      routeStore.setPostcodes(postcodes);
      bulkInput = '';
    }

    errors = newErrors;
    setTimeout(() => { errors = {}; }, 3000);
  }

  function handleSingleAdd() {
    const result = processPostcode(singleInput);

    if (result.valid) {
      postcodes = [...postcodes, result.formatted];
      routeStore.addPostcode(result.formatted);
      singleInput = '';
      delete errors.single;
    } else {
      errors = { ...errors, single: result.error };
    }
  }

  function removePostcode(index) {
    postcodes = postcodes.filter((_, i) => i !== index);
    routeStore.removePostcode(index);
  }

  function handleStartManual() {
    const result = processPostcode(startInput);

    if (result.valid) {
      startLocation = result.formatted;
      routeStore.setStartLocation({ postcode: result.formatted });
      delete errors.start;
      useCurrentLocation = false;
    } else {
      errors = { ...errors, start: result.error };
    }
  }

  async function handleCurrentLocation() {
    isLocating = true;
    useCurrentLocation = false;

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      startLocation = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      routeStore.setStartLocation(startLocation);
      useCurrentLocation = true;
      delete errors.location;
    } catch (error) {
      errors = { ...errors, location: 'Could not get your location. Please enter a postcode instead.' };
    } finally {
      isLocating = false;
    }
  }

  function handleEndManual() {
    const result = processPostcode(endInput);

    if (result.valid) {
      endLocation = result.formatted;
      routeStore.setEndLocation({ postcode: result.formatted });
      delete errors.end;
      useCurrentLocationAsEnd = false;
    } else {
      errors = { ...errors, end: result.error };
    }
  }

  async function handleCurrentLocationAsEnd() {
    isLocating = true;
    useCurrentLocationAsEnd = false;

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      endLocation = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      routeStore.setEndLocation(endLocation);
      useCurrentLocationAsEnd = true;
      delete errors.endLocation;
    } catch (error) {
      errors = { ...errors, endLocation: 'Could not get your location.' };
    } finally {
      isLocating = false;
    }
  }

  function handleClearEnd() {
    endLocation = null;
    endInput = '';
    useCurrentLocationAsEnd = false;
    routeStore.setEndLocation(null);
  }

  function handleOptimize() {
    if (canOptimize) {
      onOptimize();
    }
  }
</script>

<div class="min-h-screen" style="background: var(--bg);">
  <!-- Header with mearsit logo -->
  <header class="sticky top-0 z-50 backdrop-blur-md" style="background: rgba(250, 248, 243, 0.88); border-bottom: 1px solid var(--border);">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <button on:click={onBackToHome} class="flex items-center gap-4 hover:opacity-80 transition-opacity" style="background: none; border: none; cursor: pointer; padding: 0;">
          <img src="/mearsit-lockup.png" alt="mearsit — software · analysis · delivery" class="h-10" style="display: block;" />
        </button>
        <p style="font-size: 1rem; font-weight: 600; color: var(--fg); font-family: var(--font-display);">ScriptRunner</p>
      </div>
    </div>
  </header>

  <div class="max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
    <!-- Page Header -->
    <div class="mb-8 animate-fade-in">
      <h1 class="text-3xl sm:text-4xl font-bold mb-2" style="font-family: var(--font-display); color: var(--fg);">
        Plan Your Route
      </h1>
      <p class="text-base" style="color: var(--fg-muted);">
        Add your delivery postcodes and starting location
      </p>
    </div>

    <div class="space-y-6">
      <!-- Starting Location -->
      <Card padding="md" elevated={true}>
        <div class="mb-4">
          <h2 class="text-xl font-semibold mb-1 flex items-center gap-2" style="font-family: var(--font-body); color: var(--fg);">
            <span>Starting Location</span>
          </h2>
          <p class="text-sm" style="color: var(--fg-muted);">Where will you begin your route?</p>
        </div>

        <div class="space-y-4">
          <Button
            variant={useCurrentLocation ? 'success' : 'secondary'}
            fullWidth={true}
            disabled={isLocating}
            on:click={handleCurrentLocation}
          >
            {#if isLocating}
              Getting location...
            {:else if useCurrentLocation}
              ✓ Using Current Location
            {:else}
              Use My Current Location
            {/if}
          </Button>

          <div class="relative">
            <div class="absolute inset-0 flex items-center" aria-hidden="true">
              <div class="w-full border-t" style="border-color: var(--border);"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2" style="background: var(--bg-raised); color: var(--fg-subtle);">or</span>
            </div>
          </div>

          <div class="flex gap-2">
            <Input
              bind:value={startInput}
              placeholder="Enter starting postcode (e.g., SW1A 1AA)"
              error={errors.start || errors.location || ''}
              fullWidth={true}
              on:input={() => { useCurrentLocation = false; }}
            />
            <Button
              variant="secondary"
              on:click={handleStartManual}
              disabled={!startInput}
            >
              Set
            </Button>
          </div>

          {#if startLocation && !useCurrentLocation}
            <div class="flex items-center gap-2 text-sm" style="color: var(--fern-300);">
              <span>✓</span>
              <span>Starting from: <span class="font-mono font-semibold">{startLocation}</span></span>
            </div>
          {/if}
        </div>
      </Card>

      <!-- Bulk Input -->
      <Card padding="md">
        <div class="mb-4">
          <h2 class="text-xl font-semibold mb-1 flex items-center gap-2" style="font-family: var(--font-body); color: var(--fg);">
            <span>Delivery Postcodes</span>
          </h2>
          <p class="text-sm" style="color: var(--fg-muted);">Paste multiple postcodes (one per line) or add them individually</p>
        </div>

        <div class="space-y-4">
          <Textarea
            bind:value={bulkInput}
            placeholder="SW1A 1AA&#10;E1 6AN&#10;WC2N 5DU&#10;..."
            rows={6}
            label="Paste Postcodes"
          />

          {#if Object.keys(errors).some(key => key.startsWith('bulk-'))}
            <div class="p-3 rounded-lg" style="background: #FEE2E2; border: 1px solid #FCA5A5;">
              <p class="text-sm" style="color: #DC2626;">
                Some postcodes were invalid and skipped. Please check the format.
              </p>
            </div>
          {/if}

          <Button
            variant="secondary"
            fullWidth={true}
            on:click={handleBulkAdd}
            disabled={!bulkInput.trim()}
          >
            Add All Postcodes
          </Button>

          <div class="relative">
            <div class="absolute inset-0 flex items-center" aria-hidden="true">
              <div class="w-full border-t" style="border-color: var(--border);"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2" style="background: var(--bg-raised); color: var(--fg-subtle);">or add one at a time</span>
            </div>
          </div>

          <div class="flex gap-2">
            <Input
              bind:value={singleInput}
              placeholder="Enter postcode"
              error={errors.single || ''}
              fullWidth={true}
            />
            <Button
              variant="secondary"
              on:click={handleSingleAdd}
              disabled={!singleInput}
            >
              <span class="text-xl">+</span>
            </Button>
          </div>
        </div>
      </Card>

      <!-- Postcode List -->
      {#if postcodes.length > 0}
        <Card padding="md">
          <div class="mb-4 flex items-center justify-between">
            <h3 class="text-lg font-semibold" style="font-family: var(--font-body); color: var(--fg);">
              Added Postcodes ({postcodes.length})
            </h3>
            {#if postcodes.length >= 2}
              <span class="text-sm flex items-center gap-1" style="color: var(--fern-300);">
                <span>✓</span>
                <span>Ready to optimise</span>
              </span>
            {:else}
              <span class="text-sm" style="color: var(--fg-subtle);">
                Add at least 2 postcodes
              </span>
            {/if}
          </div>

          <div class="space-y-2 max-h-64 overflow-y-auto">
            {#each postcodes as postcode, index}
              <div
                class="flex items-center justify-between p-3 rounded-lg transition-colors"
                style="background: var(--bg-sunken);"
              >
                <div class="flex items-center gap-3">
                  <span class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold" style="background: var(--accent); color: var(--paper-0);">
                    {index + 1}
                  </span>
                  <span class="font-mono font-medium" style="color: var(--fg);">
                    {postcode}
                  </span>
                </div>
                <button
                  on:click={() => removePostcode(index)}
                  class="p-2 rounded-lg transition-colors"
                  style="color: #DC2626;"
                  aria-label="Remove {postcode}"
                >
                  <span class="text-xl">×</span>
                </button>
              </div>
            {/each}
          </div>
        </Card>
      {/if}

      <!-- End Location (Optional) -->
      <Card padding="md">
        <div class="mb-4">
          <h2 class="text-xl font-semibold mb-1 flex items-center gap-2" style="font-family: var(--font-body); color: var(--fg);">
            <span>End Location (Optional)</span>
          </h2>
          <p class="text-sm" style="color: var(--fg-muted);">
            Where to go after completing all deliveries (home, hospital check-in, etc.)
          </p>
        </div>

        <div class="space-y-4">
          <Button
            variant={useCurrentLocationAsEnd ? 'success' : 'secondary'}
            fullWidth={true}
            disabled={isLocating}
            on:click={handleCurrentLocationAsEnd}
          >
            {#if isLocating}
              Getting location...
            {:else if useCurrentLocationAsEnd}
              ✓ Using Current Location as End
            {:else}
              Use My Current Location as End
            {/if}
          </Button>

          <div class="relative">
            <div class="absolute inset-0 flex items-center" aria-hidden="true">
              <div class="w-full border-t" style="border-color: var(--border);"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2" style="background: var(--bg-raised); color: var(--fg-subtle);">or</span>
            </div>
          </div>

          <div class="flex gap-2">
            <Input
              bind:value={endInput}
              placeholder="Enter end postcode"
              error={errors.end || errors.endLocation || ''}
              fullWidth={true}
              on:input={() => { useCurrentLocationAsEnd = false; }}
            />
            <Button
              variant="secondary"
              on:click={handleEndManual}
              disabled={!endInput}
            >
              Set
            </Button>
          </div>

          {#if endLocation && !useCurrentLocationAsEnd}
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2 text-sm" style="color: var(--fern-300);">
                <span>✓</span>
                <span>Ending at: <span class="font-mono font-semibold">{endLocation}</span></span>
              </div>
              <button
                on:click={handleClearEnd}
                class="text-sm px-2 py-1 rounded transition-colors"
                style="color: var(--fg-subtle);"
              >
                Clear
              </button>
            </div>
          {/if}

          {#if useCurrentLocationAsEnd}
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2 text-sm" style="color: var(--fern-300);">
                <span>✓</span>
                <span>Will return to current location after deliveries</span>
              </div>
              <button
                on:click={handleClearEnd}
                class="text-sm px-2 py-1 rounded transition-colors"
                style="color: var(--fg-subtle);"
              >
                Clear
              </button>
            </div>
          {/if}
        </div>
      </Card>

      <!-- Optimize Button -->
      <div class="sticky bottom-4">
        <Card padding="md" elevated={true}>
          <Button
            variant="primary"
            size="lg"
            fullWidth={true}
            disabled={!canOptimize || isOptimizing}
            on:click={handleOptimize}
          >
            <span class="flex items-center justify-center gap-2">
              {#if isOptimizing}
                <span class="animate-spin text-xl">⏳</span>
                <span>Optimising Route...</span>
              {:else}
                <span>Optimise Route</span>
                <span class="text-xl">→</span>
              {/if}
            </span>
          </Button>

          {#if isOptimizing}
            <p class="text-center text-sm mt-3" style="color: var(--fg-muted);">
              Geocoding postcodes and calculating optimal route...
            </p>
          {:else if !canOptimize}
            <p class="text-center text-sm mt-3" style="color: var(--fg-subtle);">
              {#if !startLocation && !useCurrentLocation}
                Set your starting location to continue
              {:else if postcodes.length < 2}
                Add at least 2 delivery postcodes
              {/if}
            </p>
          {/if}
        </Card>
      </div>
    </div>
  </div>
</div>